<template>
  <span class="omx-bugreport">
    <button class="omx-bugreport__trigger" @click="open = true">
      <b-icon icon="bug-outline" size="is-small" />
      <span>{{ $t('shell.bug.trigger') }}</span>
    </button>

    <b-modal
        v-model="open"
        trap-focus
        :destroy-on-hide="true"
        aria-role="dialog"
        aria-modal
        class="omx-modal--narrow"
    >
      <OmxCard :title="$t('shell.bug.title')">
        <p class="omx-bugreport__intro">{{ $t('shell.bug.intro') }}</p>

        <OmxField
            v-model="summary"
            :label="$t('shell.bug.summary-label')"
            :placeholder="$t('shell.bug.summary-placeholder')"
        />
        <OmxField
            type="textarea"
            v-model="description"
            :label="$t('shell.bug.description-label')"
            :placeholder="$t('shell.bug.description-placeholder')"
        />

        <details class="omx-bugreport__context">
          <summary>{{ $t('shell.bug.context-label') }}</summary>
          <pre>{{ contextText }}</pre>
        </details>

        <template #foot>
          <div class="omx-bugreport__actions">
            <b-button icon-left="close-thick" @click="open = false">
              {{ $t('common.cancel') }}
            </b-button>
            <b-button type="is-primary" icon-left="email-send-outline" @click="send">
              {{ $t('shell.bug.send') }}
            </b-button>
          </div>
        </template>
      </OmxCard>
    </b-modal>
  </span>
</template>

<script>
import OmxCard from "@/components/OmxCard.vue";
import OmxField from "@/components/OmxField.vue";

const BUG_REPORT_EMAIL = "bugreport@bald.studio";

export default {
  name: "OmxBugReport",
  components: { OmxCard, OmxField },
  data() {
    return {
      open: false,
      summary: "",
      description: ""
    };
  },
  computed: {
    // Diagnostic context attached to every report so we don't have to ask for it.
    context() {
      let endpoint = "";
      try { endpoint = this.getSubmitEndpoint(); } catch (e) { endpoint = "n/a"; }
      return {
        app: this.$brand && this.$brand.name ? this.$brand.name : "",
        domain: window.location.hostname,
        page: window.location.href,
        endpoint,
        language: this.$i18n.locale,
        screen: `${window.innerWidth}×${window.innerHeight} @${window.devicePixelRatio || 1}x`,
        browser: navigator.userAgent,
        time: new Date().toISOString()
      };
    },
    contextText() {
      const c = this.context;
      return [
        `App: ${c.app}`,
        `Domain: ${c.domain}`,
        `Page: ${c.page}`,
        `Endpoint: ${c.endpoint}`,
        `Language: ${c.language}`,
        `Screen: ${c.screen}`,
        `Browser: ${c.browser}`,
        `Time: ${c.time}`
      ].join("\n");
    }
  },
  methods: {
    send() {
      if (!this.description.trim()) {
        this.showError(this.$t('shell.bug.empty-error'));
        return;
      }
      const app = this.context.app || "App";
      const subject = `[Bug] ${app}: ${this.summary.trim() || 'Bug report'}`;
      const body =
        `${this.description.trim()}\n\n` +
        `----------------------------------------\n` +
        `Technical details:\n${this.contextText}\n`;
      const mailto = `mailto:${BUG_REPORT_EMAIL}` +
        `?subject=${encodeURIComponent(subject)}` +
        `&body=${encodeURIComponent(body)}`;
      window.location.href = mailto;
      this.open = false;
      this.summary = "";
      this.description = "";
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.omx-bugreport__trigger {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  border: none;
  color: #7c8aa0;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 6px;
  &:hover { color: #fff; background: rgba(255,255,255,.07); }
}

.omx-bugreport__intro {
  color: var(--omx-ink-600);
  font-size: 13px;
  margin-bottom: 14px;
}

.omx-bugreport__context {
  margin-top: 12px;
  font-size: 12px;
  color: var(--omx-ink-600);
  summary { cursor: pointer; user-select: none; }
  pre {
    margin-top: 8px;
    padding: 10px;
    background: var(--omx-ink-50);
    border: 1px solid var(--omx-ink-100);
    border-radius: 8px;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 11.5px;
    color: var(--omx-ink-700);
  }
}

.omx-bugreport__actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  width: 100%;
  flex-wrap: wrap;
}
</style>
